import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCog, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const sel = 'h-9 w-full bg-zinc-900 border border-zinc-700 rounded-md text-sm text-white px-2';

export default function ProgrammeInstructorsPanel({ programmeId, headers }) {
  const [instructors, setInstructors] = useState([]);
  const [lead, setLead] = useState('');
  const [courses, setCourses] = useState([]);
  const [map, setMap] = useState({});
  const [saving, setSaving] = useState(false);

  const apply = (d) => {
    setLead(d.lead_instructor?.user_id || '');
    setCourses(d.courses || []);
    setMap(Object.fromEntries((d.courses || []).map((c) => [c.activity_id, c.instructor?.user_id || ''])));
  };

  const load = useCallback(async () => {
    try {
      const [i, a] = await Promise.all([axios.get(`${API}/admin/instructors`, { headers }), axios.get(`${API}/programmes/${programmeId}/instructors`, { headers })]);
      setInstructors(i.data.instructors || []);
      apply(a.data);
    } catch (e) { toast.error('Could not load instructors'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/programmes/${programmeId}/instructors`, { lead_instructor_id: lead || null, course_instructors: map }, { headers });
      apply(data);
      toast.success('Instructors saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save instructors'); }
    finally { setSaving(false); }
  };

  const options = instructors.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>);
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-5 space-y-5" data-testid="programme-instructors-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><UserCog className="h-5 w-5 text-teal-400" /><h3 className="text-white font-semibold">Instructors</h3></div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-instructors-btn"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
      </div>
      {instructors.length === 0 && <p className="text-xs text-amber-300" data-testid="no-instructors-hint">No users have the Instructor role yet. Set a user's role to Instructor in Admin → Users first.</p>}
      <div>
        <label className="text-xs text-zinc-400 uppercase tracking-wide">Programme lead instructor</label>
        <select className={`${sel} mt-1`} value={lead} onChange={(e) => setLead(e.target.value)} data-testid="lead-instructor-select">
          <option value="">— None —</option>{options}
        </select>
        <p className="text-xs text-zinc-500 mt-1">In charge of the whole programme: QA, marking, progress and all learner questions.</p>
      </div>
      {courses.length > 0 && (
        <div>
          <p className="text-xs text-zinc-400 uppercase tracking-wide mb-2">Course instructors (optional)</p>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {courses.map((c) => (
              <div key={c.activity_id} className="grid md:grid-cols-2 gap-2 items-center">
                <p className="text-sm text-zinc-300 truncate">{c.title} <span className="text-zinc-500 text-xs">· {c.period_title}</span></p>
                <select className={sel} value={map[c.activity_id] || ''} onChange={(e) => setMap({ ...map, [c.activity_id]: e.target.value })} data-testid={`course-instructor-select-${c.activity_id}`}>
                  <option value="">Same as lead</option>{options}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
